import { useQuery } from '@tanstack/react-query';
import { GetProcessVariableDataQuery } from 'app/__generated__/global';
import { QUERY_KEY } from 'app/business-logic/services/query-key';
import { getDistinctColours } from 'app/helpers/getDistinctColours';
import { toSignificantFigures } from 'app/helpers/toSignificantFigures';
import { FALLBACK_NUMERIC_SCALE } from 'app/redux/monitoring/constants';
import { useThemeContext } from 'app/theme/useThemeContext';
import Guid from 'core/types/Guid';
import { useCallback, useMemo } from 'react';
import { getProcessVariableSeriesData } from './getProcessVariableSeriesData';
import { EventsChartSeries } from './useEventsChartOptions.types';

export const useProcessVariableSeries = ({
  traceVariableIds,
  utcStartDate,
  utcEndDate,
}: Partial<{
  traceVariableIds: Guid[];
  utcStartDate: string;
  utcEndDate: string;
}>) => {
  const hasAllParameters = !!traceVariableIds?.length && !!utcStartDate && !!utcEndDate;
  const convertResponseToSeries = useConvertResponseToSeries();
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEY.traceVariablesSeries, utcStartDate, utcEndDate, traceVariableIds?.join(', ')],
    queryFn: () => {
      if (!hasAllParameters) return;
      return getProcessVariableSeriesData({ traceVariableIds, utcStartDate, utcEndDate });
    },
    enabled: hasAllParameters,
  });
  return useMemo(() => {
    return {
      data: convertResponseToSeries(data?.data),
      ...rest,
    };
  }, [convertResponseToSeries, data, rest]);
};
function useConvertResponseToSeries() {
  const { colors } = useThemeContext();
  return useCallback(
    (data: GetProcessVariableDataQuery | undefined) => {
      if (!data) return;
      const variables = data.facility?.processVariables ?? [];
      const seriesColours = [colors.brand.brand02, ...getDistinctColours(variables.length - 1)];
      const series: EventsChartSeries[] = variables.map(({ variableData, name, process, numericScale }, index) => ({
        type: 'line',
        data:
          variableData?.data
            .filter(filterOutNullValues)
            .map(([dateTime, value]) => [
              dateTime,
              Number(toSignificantFigures(value, numericScale ?? FALLBACK_NUMERIC_SCALE)),
            ]) ?? [],
        color: seriesColours.at(index)!,
        name,
        title: process.name,
      }));
      return series;
    },
    [colors]
  );
}

const filterOutNullValues = (
  data: [number, (number | undefined)?, (number | undefined)?]
): data is [number, number] => {
  const [, value] = data;
  return typeof value === 'number';
};
